// Loan calculation engine

export interface LoanInputs {
  msrp: number
  downPayment: number
  tradeIn: number
  taxRate: number
  dealerFees: number
  apr: number
  termMonths: number
  extraMonthly: number
}

export interface LoanResults {
  principal: number
  monthlyPayment: number
  totalPaid: number
  totalInterest: number
  acceleratedMonths: number
  acceleratedTotalInterest: number
  monthsSaved: number
  interestSaved: number
}

export interface AmortizationRow {
  month: number
  payment: number
  principalPaid: number
  interestPaid: number
  balance: number
}

export function calcLoan(inputs: LoanInputs): LoanResults {
  const {
    msrp,
    downPayment,
    tradeIn,
    taxRate,
    dealerFees,
    apr,
    termMonths,
    extraMonthly,
  } = inputs

  const taxAmount = (msrp * taxRate) / 100
  const principal = Math.max(0, msrp - downPayment - tradeIn + taxAmount + dealerFees)

  const r = apr / 12 / 100
  let monthlyPayment = 0

  if (r === 0) {
    monthlyPayment = principal / termMonths
  } else {
    monthlyPayment = (principal * r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1)
  }

  const totalPaid = monthlyPayment * termMonths
  const totalInterest = totalPaid - principal

  // Payoff acceleration
  let balance = principal
  let accelMonths = 0
  let accelInterest = 0

  while (balance > 0 && accelMonths < termMonths * 2) {
    const interestCharge = balance * r
    const principalCharge = Math.min(monthlyPayment - interestCharge + extraMonthly, balance)
    balance -= principalCharge
    accelInterest += interestCharge
    accelMonths++
    if (balance <= 0.01) break
  }

  const monthsSaved = Math.max(0, termMonths - accelMonths)
  const interestSaved = Math.max(0, totalInterest - accelInterest)

  return {
    principal,
    monthlyPayment,
    totalPaid,
    totalInterest,
    acceleratedMonths: accelMonths,
    acceleratedTotalInterest: accelInterest,
    monthsSaved,
    interestSaved,
  }
}

export function buildAmortization(
  principal: number,
  apr: number,
  termMonths: number,
  monthlyPayment: number
): AmortizationRow[] {
  const r = apr / 12 / 100
  const rows: AmortizationRow[] = []
  let balance = principal

  for (let m = 1; m <= termMonths && balance > 0.01; m++) {
    const interestPaid = balance * r
    const principalPaid = Math.min(monthlyPayment - interestPaid, balance)
    balance -= principalPaid
    rows.push({
      month: m,
      payment: monthlyPayment,
      principalPaid,
      interestPaid,
      balance: Math.max(0, balance),
    })
  }

  return rows
}

export function fmt(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function fmtShort(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}
